import { FilesPanel } from '@odo/shared-ui';
import type { Asset, FilesCapabilities } from '@odo/shared-ui';
import { useCallback, useMemo } from 'react';
import api from '../../../../../api/config';
import { usePracticeDocuments } from '../../../../../hooks/usePracticeDocuments';
import { practiceFilesApi } from '../../../../../services/practiceFilesApi';
import { practiceDocumentToAsset } from './practiceDocumentToAsset';
import { PRACTICE_FILE_CATEGORY_CONFIG } from './practiceFileCategoryConfig';
import { PracticeFileUploadForm } from './PracticeFileUploadForm';

interface PracticeFilesSectionProps {
    practiceId: string;
}

// The backend has no PATCH for practice documents (see services/practiceDocuments.ts)
// and the shared module's generic batch upload zone has no fields for our
// documentType/description, so uploads go through PracticeFileUploadForm -
// rendered via FilesPanel's `uploadSection` slot behind the toolbar's Upload
// toggle (same open/close rhythm as the patient page), replacing the
// module's own drop zone.
const CAPABILITIES: FilesCapabilities = {
    canDelete: true,
    canRename: false,
    canReclassify: false,
    canUpload: true,
    showUploadedBy: false,
    enablePreviewNavigation: true,
    tooltips: 'radix'
};

// The Details tab's "Files" rail pane - upload form up top, the shared files
// module below for browsing/preview/download/delete (categorized as
// Agreements / Other documents via practiceFileCategoryConfig). Wrapped in
// its own card since the Details tab no longer has an outer wrapping box
// (see DetailsTab) - the page itself scrolls now, not this pane.
export function PracticeFilesSection({ practiceId }: PracticeFilesSectionProps) {
    const { documents, isLoading, isError, error, refetch } = usePracticeDocuments(practiceId);

    const assets = useMemo(() => {
        const map: Record<string, Asset> = {};
        documents.forEach((doc) => {
            map[doc.id] = practiceDocumentToAsset(doc);
        });
        return map;
    }, [documents]);

    const refresh = useCallback(async () => {
        await refetch();
    }, [refetch]);

    // No card padding - FilesPanel's toolbar/content carry their own px-5, so
    // padding here would double up and indent the toolbar's search input off
    // the card edge.
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] flex flex-col flex-1 min-h-0 overflow-hidden">
            {/* No static header - the module's toolbar (search + Select/Upload/
                layout controls) is the pane's header. No shrink-0 here -
                otherwise the flex column compresses them to fit instead of letting
                the panel take the remaining space and scroll internally (see
                DetailGroupCard.tsx's comment on the same compression bug in this
                tab). FilesPanel manages its own `flex-1 min-h-0` + internal scroll. */}
            <FilesPanel
                patientId={practiceId}
                assets={assets}
                isLoading={isLoading}
                error={isError ? error : undefined}
                refresh={refresh}
                api={practiceFilesApi}
                capabilities={CAPABILITIES}
                categoryConfig={PRACTICE_FILE_CATEGORY_CONFIG}
                uploadSection={<PracticeFileUploadForm practiceId={practiceId} />}
                viewerApiBaseUrl={api.defaults.baseURL ?? ''}
                getAuthToken={() => localStorage.getItem('odo_login_token')}
                storageKeyPrefix="practice_files"
            />
        </div>
    );
}
